import React from 'react';
import dayjs from 'dayjs';
import { Divider, FormHelperText } from '@mui/material';
import { ExpandMore, Delete, AddCircle } from '@mui/icons-material';
import MuiDatePicker from '../../../../../../../../components/FormFields/MuiDatePicker';
import MuiSelectOptions from '../../../../../../../../components/FormFields/MuiSelectOptions';
import CustomSlotInput from '../../../../../../../../components/FormFields/CustomSlotInput';
import MuiTimePicker from '../../../../../../../../components/FormFields/MuiTimePicker';
import { formatSlotDate } from '../../../../../../../../utils/dateFormatter';
import { H6, H5, Body1, Body3, Body2 } from '../../../../../../../../themes/typography';
import '../../../../../../../../styles/components/Accordion.css';

const slotOptions = [
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 180, label: '3 hours' },
    { value: 240, label: '4 hours' },
    { value: 300, label: '5 hours' }
];

function MultipleDateEvent({
    formik,
    handleDateSelection,
    handleDateDeletion,
    handleCloseDatePicker,
    isDatePickerClosed,
    expanded,
    toggleAccordion,
    addSlotForDate,
    deleteSlotForDate,
    handleSlotDurationChange,
    slotDurations,
    handleCustomDurationChange,
    customSlotDurations,
    timeUnits,
    setTimeUnits,
    durationErrors,
    isEditModeOn
}) {
    const format =
        formik.values.dates.length > 0
            ? `${formik.values.dates[0]} - ${formik.values.dates[formik.values.dates.length - 1]}`
            : 'DD/MM/YYYY - DD/MM/YYYY';

    return (
        <form
            className="onetime-event-schedule-container"
            onSubmit={formik?.handleSubmit}
            noValidate
        >
            <div className="start-date-schedule-wrapper">
                <div className="start-date-schedule-container">
                    <div className="schedule-selection-title">
                        <H6>Date Schedule</H6>
                    </div>
                    <div className="start-date-time-container">
                        <div className="start-date-input-container">
                            <MuiDatePicker
                                fullWidth
                                showToolbar
                                format={format}
                                label="Select Dates *"
                                closeOnSelect={false}
                                disabled={!isEditModeOn}
                                minDate={dayjs(new Date())}
                                selectedDates={formik.values.dates}
                                onClose={handleCloseDatePicker}
                                value={dayjs() ? 'DD/MM/YYYY' : dayjs()}
                                onChange={(date) => handleDateSelection(date)}
                                onDeleteDate={(date) => handleDateDeletion(date)}
                                error={formik.touched.dates && Boolean(formik.errors.dates)}
                                helperText={formik.touched.dates && formik.errors.dates}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {isDatePickerClosed &&
                formik.values?.schedules?.map((schedule, scheduleIndex) => (
                    <div className="accordion" key={scheduleIndex}>
                        {/* Accordion Header */}
                        <div className="custom-accordion-header">
                            <div className="custom-accordion-title-and-icon">
                                <div className="accordion-header-picker-container">
                                    {/* Accordion Date */}
                                    <div className="accordion-header-date">
                                        <H5>{formatSlotDate(schedule?.date)}</H5>
                                    </div>

                                    {/* Date Start & End Time */}
                                    <div className="timing-input-container">
                                        <div className="accordion-header-timing-container">
                                            <MuiTimePicker
                                                label="Start Time *"
                                                disabled={!isEditModeOn}
                                                id={`start-time-${schedule.date}`}
                                                value={
                                                    schedule.startTime
                                                        ? dayjs(schedule.startTime)
                                                        : null
                                                }
                                                minTime={
                                                    dayjs(schedule.date, 'DD/MM/YYYY').isSame(
                                                        dayjs(),
                                                        'day'
                                                    )
                                                        ? dayjs().add(1, 'hour')
                                                        : dayjs().startOf('day')
                                                }
                                                onChange={(newValue) =>
                                                    formik.setFieldValue(
                                                        `schedules.${scheduleIndex}.startTime`,
                                                        newValue
                                                    )
                                                }
                                                error={
                                                    formik.touched.schedules?.[scheduleIndex]
                                                        ?.startTime &&
                                                    Boolean(
                                                        formik.errors.schedules?.[scheduleIndex]
                                                            ?.startTime
                                                    )
                                                }
                                                helperText={
                                                    formik.touched.schedules?.[scheduleIndex]
                                                        ?.startTime &&
                                                    formik.errors.schedules?.[scheduleIndex]
                                                        ?.startTime
                                                }
                                            />
                                        </div>
                                        <div className="accordion-header-timing-container">
                                            <MuiTimePicker
                                                label="End Time *"
                                                disabled={!isEditModeOn}
                                                id={`end-time-${schedule.date}`}
                                                value={
                                                    schedule.endTime
                                                        ? dayjs(schedule.endTime)
                                                        : null
                                                }
                                                minTime={
                                                    schedule.startTime
                                                        ? dayjs(schedule.startTime).add(5, 'minute')
                                                        : dayjs().startOf('day')
                                                }
                                                onChange={(newValue) =>
                                                    formik.setFieldValue(
                                                        `schedules.${scheduleIndex}.endTime`,
                                                        newValue
                                                    )
                                                }
                                                error={
                                                    formik.touched.schedules?.[scheduleIndex]
                                                        ?.endTime &&
                                                    Boolean(
                                                        formik.errors.schedules?.[scheduleIndex]
                                                            ?.endTime
                                                    )
                                                }
                                                helperText={
                                                    formik.touched.schedules?.[scheduleIndex]
                                                        ?.endTime &&
                                                    formik.errors.schedules?.[scheduleIndex]
                                                        ?.endTime
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Accordion Expand */}
                                <div className="expand-icon-container align-item-center">
                                    <div
                                        className="expand-more-icon"
                                        style={{ pointerEvents: isEditModeOn ? 'auto' : 'none' }}
                                        onClick={() => {
                                            if (!isEditModeOn) return;
                                            toggleAccordion(schedule?.date);
                                        }}
                                    >
                                        <ExpandMore
                                            sx={{
                                                color: '#f5f5f5',
                                                fontSize: '1.5rem',
                                                transition: 'all 0.3s ease-in-out',
                                                transform: expanded[schedule.date]
                                                    ? 'rotate(180deg)'
                                                    : 'rotate(0deg)'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Number of slots & Add Slot */}
                            <div
                                className="slots-overview-container"
                                style={{ display: expanded[schedule.date] ? 'none' : '' }}
                            >
                                <div className="no-of-slots-container">
                                    <Body3>
                                        Number of slots:{' '}
                                        <span style={{ color: '#f8a21e' }}>
                                            {schedule.slots?.length || 0}
                                        </span>
                                    </Body3>
                                </div>
                                <div
                                    className="add-slots-container"
                                    style={{ pointerEvents: isEditModeOn ? 'auto' : 'none' }}
                                    onClick={() => {
                                        if (!isEditModeOn) return;
                                        addSlotForDate(schedule?.date);
                                    }}
                                >
                                    <Body3>Add Slots</Body3>
                                    <AddCircle style={{ color: '#f8a21e' }} />
                                </div>
                            </div>
                        </div>

                        {/* Accordion Details */}
                        <div
                            className="accordion-details-container"
                            style={{ display: !expanded[schedule.date] ? 'none' : '' }}
                        >
                            <div className="slots-overview-container">
                                <div className="slot-duration-selection-container">
                                    <div className="duration-selection-wrapper">
                                        {/* Slot Option Selection */}
                                        <div className="slot-option-selection">
                                            <MuiSelectOptions
                                                id={`duration-${schedule.date}`}
                                                label="Slot Duration *"
                                                options={slotOptions}
                                                value={slotDurations[schedule.date] || ''}
                                                onChange={(e) =>
                                                    handleSlotDurationChange(
                                                        schedule.date,
                                                        e.target.value
                                                    )
                                                }
                                                disabled={!isEditModeOn}
                                            />
                                        </div>

                                        {/* OR */}
                                        <div className="or-container">
                                            <Body2>OR</Body2>
                                        </div>

                                        {/* Custom Slot Input */}
                                        <div className="slot-duration-container">
                                            <CustomSlotInput
                                                id={`customInput-${schedule.date}`}
                                                label="Custom Input"
                                                value={customSlotDurations[schedule.date] || ''}
                                                isHourSelected={timeUnits[schedule.date]}
                                                setIsHourSelected={(unit) =>
                                                    setTimeUnits((prev) => ({
                                                        ...prev,
                                                        [schedule.date]: unit
                                                    }))
                                                }
                                                onChange={(e) =>
                                                    handleCustomDurationChange(
                                                        schedule.date,
                                                        e.target.value
                                                    )
                                                }
                                                disabled={!isEditModeOn}
                                            />
                                        </div>
                                    </div>

                                    {/* Duration Error */}
                                    {durationErrors[schedule.date] && (
                                        <FormHelperText error={true}>
                                            Custom duration should not be less than 5 minutes
                                        </FormHelperText>
                                    )}
                                </div>

                                {/* Main Add Slot Button */}
                                <div
                                    className="add-slots-container"
                                    style={{ pointerEvents: isEditModeOn ? 'auto' : 'none' }}
                                    onClick={() => {
                                        if (!isEditModeOn) return;
                                        addSlotForDate(schedule?.date);
                                    }}
                                >
                                    <Body1>Add Slots</Body1>
                                    <AddCircle style={{ color: '#f8a21e' }} />
                                </div>
                            </div>

                            {/* Slots Listing Container */}
                            <div className="slots-listing-container">
                                {schedule.slots?.map((slot, slotIndex) => (
                                    <React.Fragment key={slotIndex}>
                                        <div className="accordion-children-node-wrapper">
                                            {/* Slot Index */}
                                            <div className="slot-number-container">
                                                <Body1>{`Slot ${slotIndex + 1}`}</Body1>
                                            </div>

                                            {/* Start Time */}
                                            <div className="timing-container">
                                                <MuiTimePicker
                                                    id={`start-time-${schedule.date}-${slotIndex}`}
                                                    label="Start Time"
                                                    disabled={!isEditModeOn}
                                                    value={
                                                        slot.startTime
                                                            ? dayjs(slot.startTime)
                                                            : null
                                                    }
                                                    onChange={(newValue) => {
                                                        formik.setFieldValue(
                                                            `schedules.${scheduleIndex}.slots.${slotIndex}.startTime`,
                                                            newValue
                                                        );
                                                    }}
                                                    minTime={
                                                        slotIndex === 0
                                                            ? dayjs(schedule.startTime)
                                                            : dayjs(
                                                                  schedule.slots[slotIndex - 1]
                                                                      .endTime
                                                              )
                                                    }
                                                    maxTime={dayjs(schedule.endTime)}
                                                    error={
                                                        formik.touched.schedules?.[scheduleIndex]
                                                            ?.slots?.[slotIndex]?.startTime &&
                                                        Boolean(
                                                            formik.errors.schedules?.[scheduleIndex]
                                                                ?.slots?.[slotIndex]?.startTime
                                                        )
                                                    }
                                                    helperText={
                                                        formik.touched.schedules?.[scheduleIndex]
                                                            ?.slots?.[slotIndex]?.startTime &&
                                                        formik.errors.schedules?.[scheduleIndex]
                                                            ?.slots?.[slotIndex]?.startTime
                                                    }
                                                />
                                            </div>

                                            {/* End Time */}
                                            <div className="timing-container">
                                                <MuiTimePicker
                                                    id={`end-time-${schedule.date}-${slotIndex}`}
                                                    label="End Time"
                                                    disabled={!isEditModeOn}
                                                    value={
                                                        slot.endTime ? dayjs(slot.endTime) : null
                                                    }
                                                    onChange={(newValue) => {
                                                        formik.setFieldValue(
                                                            `schedules.${scheduleIndex}.slots.${slotIndex}.endTime`,
                                                            newValue
                                                        );
                                                    }}
                                                    minTime={dayjs(slot.startTime).add(5, 'minute')}
                                                    maxTime={dayjs(schedule.endTime)}
                                                    error={
                                                        formik.touched.schedules?.[scheduleIndex]
                                                            ?.slots?.[slotIndex]?.endTime &&
                                                        Boolean(
                                                            formik.errors.schedules?.[scheduleIndex]
                                                                ?.slots?.[slotIndex]?.endTime
                                                        )
                                                    }
                                                    helperText={
                                                        formik.touched.schedules?.[scheduleIndex]
                                                            ?.slots?.[slotIndex]?.endTime &&
                                                        formik.errors.schedules?.[scheduleIndex]
                                                            ?.slots?.[slotIndex]?.endTime
                                                    }
                                                />
                                            </div>

                                            {/* Delete Slot */}
                                            <div
                                                className="delete-slot-action"
                                                style={{
                                                    pointerEvents: isEditModeOn ? 'auto' : 'none'
                                                }}
                                                onClick={() => {
                                                    if (!isEditModeOn) return;
                                                    deleteSlotForDate(schedule?.date, slotIndex);
                                                }}
                                            >
                                                <Delete
                                                    style={{
                                                        fontSize: '1.5rem',
                                                        color: '#f8a21e'
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {slotIndex < schedule?.slots.length - 1 && (
                                            <Divider sx={{ borderColor: 'var(--divider-color)' }} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
        </form>
    );
}

export default MultipleDateEvent;
